import { Box, Container, Grid, Typography } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { About_IMG } from '../../Constants/ImageConstants';
import { COLORS } from '../../Constants/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const MotionImageSection = () => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const upwardY = useTransform(scrollYProgress, [0, 1], [150, -150]);
  const downwardY = useTransform(scrollYProgress, [0, 1], [-150, 150]);
  const yTranslate = useTransform(scrollYProgress, [0, 1], [30, -30]);

  const coreValuesVission = [
    {
      title: 'Joyful Childhoods ',
      description:
      'Encouraging children to discover, explore, and enjoy movement through safe and engaging sports experiences.'
    },
    {
      title: 'Lifelong Learning ',
      description:
      'Developing values, leadership, resilience, teamwork, and discipline that extend far beyond the playing field. '
    },
    {
      title: 'Strong Communities',
      description:
      'Building a culture where schools, families, coaches, and communities work together to make sport accessible to every child.'
    },
  ];

  return (
    <Box
      ref={ref}
      sx={{
        pt: { xs: 10, md: 20 },
        pb: 0,
        position: 'relative',
        overflow: 'hidden',
        background: COLORS.white,
        color: COLORS.pureBlack,
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          height: { xs: 400, sm: 500, md: 700 },
          backgroundColor: COLORS.white,
        }}
      >
        <Box
          component={motion.img}
          src={About_IMG.motionsection1}
          alt="Tennis"
          sx={{
            position: 'absolute',
            right: { xs: 20, md: 160 },
            top: 40,
            width: { xs: '80%', md: '55%' },
            borderRadius: 2,
            rotate: '-6deg',
            zIndex: 2,
          }}
          style={{ y: upwardY }}
        />

        <Box
          component={motion.img}
          src={About_IMG.motionsection2}
          alt="Basketball"
          sx={{
            position: 'absolute',
            left: { xs: 20, md: 160 },
            top: 0,
            width: { xs: '80%', md: '55%' },
            borderRadius: 2,
            rotate: '6deg',
            zIndex: 1,
          }}
          style={{ y: downwardY }}
        />
      </Container>

      <Container maxWidth="xl" sx={{ py: 6, backgroundColor: COLORS.greyBG }}>
        <Grid container spacing={4} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography variant="h5" sx={{ textTransform: 'uppercase', mb: 1 }}>
                OUR VISION
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Typography variant="h3" sx={{ mb: 3 }}>
              A Future Where Every Child Experiences the Joy of Sport 
              </Typography>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography variant="body2" sx={{ color: COLORS.accentBlackTransparent }}>
              We envision a future where every child has access to quality Sports & Physical Education, inspiring coaches, and supportive communities that help them become confident, happy, healthy, and responsible individuals. 
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="xl" sx={{ pt: 6, pb: 6, backgroundColor: COLORS.greyBG }}>
        <Grid container spacing={4}>
          {coreValuesVission.map((value, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.3 }}
                viewport={{ once: true }}
              >
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 40,
                      color: COLORS.accentLime,
                      bgcolor: COLORS.accentDarkGreen,
                      borderRadius: '50%',
                      p: 0.5,
                    }}
                  />
                  <Box>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.accentBlackTransparent, fontSize: { xs: '14px', md: '15px' } }}>
                      {value.description}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="xl">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component={motion.img}
              src={About_IMG.aboutLast}
              alt="Story Image"
              sx={{
                width: '100%',
                maxWidth: 550,
                height: { xs: 300, sm: 400, md: 700 },
                borderRadius: 2,
                objectFit: 'cover',
                display: 'block',
                mx: 'auto',
              }}
              style={{ y: yTranslate }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography variant="h5" sx={{ color: COLORS.pureBlack, mb: 1 }}>
                Our Story
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Typography variant="h3" sx={{ mb: 2 }}>
                Inspired by Purpose. Built for Impact.
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography variant="body2" sx={{ color: COLORS.accentBlackTransparent }}>
              Grassroots Sports Education & Services was founded with a simple belief—that every child deserves access to meaningful Sports & Physical Education regardless of background or ability. 
              </Typography>
              <br/>
              <Typography variant="body2" sx={{ color: COLORS.accentBlackTransparent }}>
              Recognizing the need for structured school sports, qualified coaches, and curriculum-driven learning, we set out to build an organization that supports schools beyond coaching alone. Today, our vision extends across school partnerships, coach education, sports recruitment, events, sports science, and innovative digital learning solutions—all working together to strengthen grassroots sports from the ground up. 
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="xl" sx={{ mt: 6 }}>
        <Grid
          container
          alignItems="stretch"
          spacing={0}
          direction={{ xs: 'column-reverse', md: 'row' }}
          sx={{ backgroundColor: COLORS.accentDarkGreen, borderRadius: 2 }}
        >
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'left', px: { xs: 2, md: 6 }, py: { xs: 4, md: 6 } }}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="h5" sx={{ color: COLORS.white, mb: 3, letterSpacing: 1 }}>
                  WHAT SETS US APART
                </Typography>
              </motion.div>

              {[
                {
                  title: 'Curriculum-Aligned Sports Education',
                  desc: 'Structured Sports & Physical Education programs from KG to Grade 10 that integrate seamlessly into the school timetable.',
                },
                {
                  title: 'Child-Centric Learning',
                  desc: 'Every session is designed to make learning active, enjoyable, safe, and developmentally appropriate.',
                },
                {
                  title: 'Multimedia Learning Platform',
                  desc: 'Image, text, and video-based curriculum supported by assessments and digital learning resources.',
                },
                {
                  title: 'Complete Sports Ecosystem',
                  desc: 'From coach education and recruitment to sports science, infrastructure, events, and branding, we provide end-to-end sports solutions.',
                },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 * (i + 1) }}
                  viewport={{ once: true }}
                >
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="h6" sx={{ color: COLORS.white, fontWeight: 700, mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.75 }}
                viewport={{ once: true }}
              >
                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.25)' }}>
                  <Typography variant="h5" sx={{ color: COLORS.white, mb: 1.5, letterSpacing: 1 }}>
                    OUR COMMITMENT
                  </Typography>
                  <Typography variant="h6" sx={{ color: COLORS.white, fontWeight: 700, mb: 1.5 }}>
                    Creating Happy, Healthy &amp; Confident Children Through Sport
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.8 }}>
                    Every child deserves the opportunity to move, play, learn, and thrive. At Grassroots Sports Education &amp; Services, we are committed to helping schools build a culture where sport becomes an essential part of education and every child discovers the joy of lifelong physical activity.
                  </Typography>
                </Box>
              </motion.div>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Box
              component="img"
              src={About_IMG.aboutEnroll}
              alt="Basketball"
              sx={{
                width: '100%',
                height: '100%',
                minHeight: { xs: 300, md: 'unset' },
                objectFit: 'cover',
                borderRadius: 2,
                display: 'block',
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MotionImageSection;
